import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import { useCallback } from 'react';
import { Box, Typography, List, ListItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectTeamMembersHash } from 'selectors';
import { useUpdateAccountAttributesMutation } from 'TeamModule/query/hooks/useUpdateAccountAttributes';

export interface UseSetCsmAccountModalOptions {
  /** Tenant name for API context - if not provided, falls back to global tenant */
  tenant?: string;
  /** Realm ID for API context - if not provided, falls back to global realm */
  realmId?: string;
}

export const useSetCsmAccountModal = (options?: UseSetCsmAccountModalOptions) => {
  const { openModal } = useDynamicModal();
  const membersHash = useSelector(selectTeamMembersHash);
  const { tenant, realmId } = options || {};
  const { updateAccountAttributes } = useUpdateAccountAttributesMutation({ tenant, realmId });

  const openSetCsmAccountModal = useCallback(
    (args: { accountId: number }) => {
      const onConfirmSetCsmAccount = () => {
        updateAccountAttributes({
          account_id: args.accountId,
          is_csm: true,
        });
      };

      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          return (
            <SimpleModal
              onClose={toggle}
              open
              onConfirm={onConfirmSetCsmAccount}
              closeOnConfirm
              Body={
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to mark this as a CSM account?
                  </Typography>

                  <Box sx={{ my: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {membersHash[args.accountId]?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {membersHash[args.accountId]?.email}
                    </Typography>
                  </Box>

                  <Box sx={{ my: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      This action will:
                    </Typography>
                    <List sx={{ listStyleType: 'disc', pl: 4 }}>
                      <ListItem sx={{ display: 'list-item', py: 0.5, px: 0 }}>
                        <Typography variant="body2">Mark this account as a CSM account</Typography>
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', py: 0.5, px: 0 }}>
                        <Typography variant="body2">
                          Exclude this account from the active member count
                        </Typography>
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', py: 0.5, px: 0 }}>
                        <Typography variant="body2">
                          Remove this account from the Mosaic team member table
                        </Typography>
                      </ListItem>
                    </List>
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        fontWeight: 700,
                        mt: 2,
                      }}
                    >
                      This cannot be undone on galaxy
                    </Typography>
                  </Box>
                </Box>
              }
            />
          );
        },
      });
    },
    [membersHash, openModal, updateAccountAttributes],
  );
  return {
    openSetCsmAccountModal,
  };
};
